<script lang="ts">
  import type { AppConfig } from "@/config"
  import { checkTimedOut } from "@/libs/http"
  import { t } from "@/libs/i18n"
  import { filterObj } from "@/libs/lodash"
  import { ICON, Icon } from "@/libs/ui/Icon"
  import Message from "@/libs/ui/Message/Message.svelte"
  import Progress from "@/libs/ui/Progress"
  import QrCode from "@/libs/ui/QRCode/QRCode.svelte"
  import Spinner from "@/libs/ui/Spinner/Spinner.svelte"
  import { createConnection } from "@/libs/webrtc/webrtc"
  import { getApplicationStore } from "@/stores/application"
  import { onMount, type ComponentProps } from "svelte"
  import type { HTMLAttributes } from "svelte/elements"
  import { createSession, setSessionOffer, uploadSessionStarted, waitSessionStart } from "./api"
  import { analytics } from "@/libs/telemetry"

  let {
    onDone,
    onStart,
    onAbort,
    footer,
    ...props
  }: {
    onDone: (files: File[]) => void
    onStart: () => void
    onAbort: () => void
  } & HTMLAttributes<HTMLElement> &
    Pick<ComponentProps<typeof Message>, "footer"> = $props()

  const DEFAULT_QR_SIZE = 230

  const applicationStore = getApplicationStore()

  const abortController = new AbortController()
  let connection: ReturnType<typeof createConnection> | undefined = $state()

  let session: string | null = $state(null)
  let isFailed = $state(false)
  let progress = $state(0)
  let qrCodeWidth = $state(DEFAULT_QR_SIZE)

  let isDone = $derived(progress >= 1)

  const listenSessionStart = async (session: string) => {
    try {
      return await waitSessionStart(session, { signal: abortController.signal })
    } catch (err) {
      if (checkTimedOut(err as Response)) return await listenSessionStart(session)
      return null
    }
  }
  const establishConnection = (config: RTCConfiguration) => {
    const received: {
      files: {
        type: string
        name: string
        size: number
        parts: ArrayBuffer[]
        receivedSize: number
      }[]
      readonly progress: number
    } = {
      files: [],
      get progress() {
        return (
          this.files.reduce((acc, f) => acc + f.receivedSize, 0) /
          this.files.reduce((acc, f) => acc + f.size, 0)
        )
      },
    }
    let lastFileIndex = 0

    connection = createConnection(config, {
      label: "companion",
      iceTimeout: 3000,
      onOpen: onStart,
      onMessage(data, channel) {
        if (typeof data !== "string") {
          const file = received.files[lastFileIndex]
          file.parts.push(data)
          file.receivedSize += data.byteLength
          progress = received.progress

          if (progress >= 1) channel.send(JSON.stringify({ type: "done" }))
          else if (file.receivedSize >= file.size) lastFileIndex++

          return
        }

        const parsed = JSON.parse(data)

        if (parsed.type === "metadata") {
          received.files = (
            parsed.files as Pick<(typeof received)["files"][number], "name" | "size" | "type">[]
          ).map((f) => ({
            ...f,
            receivedSize: 0,
            parts: [],
          }))
        }
      },
      onClose() {
        if (isDone)
          onDone(received.files.map((f) => new File([new Blob(f.parts)], f.name, { type: f.type })))
        else {
          onAbort()
          establishConnection(config)
        }
      },
    })
  }

  const searchParams = __PROD__ ? null : new URLSearchParams(parent.location.search)

  const initUploadSession = () => {
    isFailed = false

    const videoUrl = searchParams?.get("video")

    createSession(
      {
        appConfig: {
          ...(filterObj($applicationStore.config, (prop) =>
            Array.isArray(prop) ? prop.length > 0 : Boolean(prop),
          ) as AppConfig),
          ...(!__PROD__ && videoUrl ? { videoUrl } : {}),
        },
      },
      { signal: abortController.signal },
    )
      .then(({ id, iceServers }) => {
        establishConnection({ iceServers })
        session = id
      })
      .catch(() => {
        isFailed = true
        analytics.error_screen_shown()
      })
  }

  onMount(() => {
    initUploadSession()

    return () => {
      connection?.close()
      abortController.abort("Unmounting companion invite")
    }
  })

  const sendSessionOffer = (session: string) => {
    if (!$connection?.isWaiting || !$connection.localSessionConfig) return

    setSessionOffer(
      {
        id: session,
        offer: $connection.localSessionConfig.sessionDescription,
        iceCandidates: $connection.localSessionConfig.iceCandidates,
      },
      { signal: abortController.signal },
    )
  }

  $effect(() => {
    if ($connection?.isWaiting) progress = 0
  })
  $effect(() => {
    if (!isDone && session && $connection?.localSessionConfig) sendSessionOffer(session)
  })

  $effect(() => {
    if (!isDone && session && $connection?.isWaiting && !$connection.isConnected)
      listenSessionStart(session).then((response) => {
        if (!response) {
          isFailed = true
          analytics.error_screen_shown()
          return
        }

        connection?.setRemote({
          sessionDescription: response.answer,
          iceCandidates: response.iceCandidates,
        })
      })
  })

  $effect(() => {
    if (session && $connection?.isConnected) uploadSessionStarted(session)
  })

  let shouldDisplayLink = $derived(!__PROD__ && searchParams?.get("companionLink") == "true")

  const getLink = (session: string) => {
    const companionUrl = __PROD__
      ? __COMPANION_URL__
      : (searchParams?.get("companionUrl") ?? __COMPANION_URL__)

    return `${companionUrl}/?session=${session}`
  }
  let link = $derived(session ? getLink(session) : null)
</script>

{#if progress > 0}
  <article {...props} class={["flex w-full h-full flex-col items-center justify-center", props.class]}>
    <Progress value={progress} class="w-1/2 min-w-[9rem] max-w-[15rem]" />
  </article>
{:else}
  {#if shouldDisplayLink && link}
    <a class="absolute left-2 top-2" href={link} target="_blank">link</a>
  {/if}

  <Message
    title={$connection?.isConnected ? $t.companion.active_title : $t.companion.invite_title}
    description={$connection?.isConnected ? undefined : $t.companion.invite_description}
    class={[props.class, "!max-w-full"]}
    titleClass="!text-3xl"
    descriptionClass="!text-2xl"
    {footer}
  >
    <div
      class="relative mb-10 flex size-[16rem] items-center justify-center overflow-hidden rounded-[2.5rem] bg-[rgb(var(--color))] text-[rgb(var(--background-color))]"
      class:qr-code={/* anchor for external customization */ true}
      bind:clientWidth={qrCodeWidth}
    >
      {#if isFailed}
        <button
          onclick={initUploadSession}
          title={$t.source_selection.retry_generating_qr}
          aria-label={$t.source_selection.retry_generating_qr}
          class="p-4"
        >
          <Icon type={ICON.ARROW_2_CIRCLEPATH} size={48} />
        </button>
      {:else if !session}
        <Spinner />
      {:else if link}
        <QrCode
          value={link}
          size={qrCodeWidth}
          alt={$t.companion.invite_title}
          class="size-full p-2"
        />
      {/if}
    </div>
  </Message>
{/if}
